/**
 * LCS word diff — the "what actually changed" renderer behind the Phase-10
 * change review.
 *
 * WHY A HAND-ROLLED DIFF. The admin SPA needs a before/after compare for course
 * titles, descriptions, learn-points and article bodies. `diff`/`jsdiff` would
 * do it, but this lives in `@mlm/shared`, which every app and the API import;
 * adding a runtime dependency there to render one dialog is a poor trade for
 * ~60 lines of textbook LCS.
 *
 * WORD level, not character level: a reviewer reading "₱1,000 → ₱1,200" or a
 * retitled course wants to see the words that moved, and a character diff of
 * prose produces confetti. Whitespace rides with the token that precedes it so
 * joining the segments reproduces the input exactly.
 */

export type DiffOp = 'equal' | 'insert' | 'delete';

export type DiffSegment = {
  op: DiffOp;
  text: string;
};

/**
 * Split into words, keeping the trailing whitespace on each token. `'a  b'`
 * becomes `['a  ', 'b']`, so `segments.map(s => s.text).join('')` on an
 * all-equal diff is the original string, byte for byte.
 */
export function tokenizeWords(input: string): string[] {
  if (!input) return [];
  return input.match(/\S+\s*|\s+/g) ?? [];
}

/**
 * Guard rail. LCS is O(n × m) in time AND memory, and a 100 KB article body is
 * ~15k words each side — 225M cells, which would take the admin's browser down.
 * Past the cap we degrade to a single delete + insert pair, which still renders
 * a correct (if coarse) before/after.
 */
const MAX_TOKENS = 2500;

export function diffWords(before: string, after: string): DiffSegment[] {
  if (before === after) {
    return before ? [{ op: 'equal', text: before }] : [];
  }
  const a = tokenizeWords(before);
  const b = tokenizeWords(after);
  if (!a.length) return b.length ? [{ op: 'insert', text: after }] : [];
  if (!b.length) return [{ op: 'delete', text: before }];
  if (a.length > MAX_TOKENS || b.length > MAX_TOKENS) {
    return [
      { op: 'delete', text: before },
      { op: 'insert', text: after },
    ];
  }

  // Classic LCS table. Row-major, (a.length + 1) × (b.length + 1).
  const width = b.length + 1;
  const lcs = new Uint16Array((a.length + 1) * width);
  for (let i = a.length - 1; i >= 0; i -= 1) {
    for (let j = b.length - 1; j >= 0; j -= 1) {
      lcs[i * width + j] =
        a[i] === b[j]
          ? lcs[(i + 1) * width + j + 1]! + 1
          : Math.max(lcs[(i + 1) * width + j]!, lcs[i * width + j + 1]!);
    }
  }

  const out: DiffSegment[] = [];
  // Runs of the same op are merged as they are emitted, so the renderer gets
  // one `<del>` per deleted phrase rather than one per word.
  const push = (op: DiffOp, text: string) => {
    const last = out[out.length - 1];
    if (last && last.op === op) last.text += text;
    else out.push({ op, text });
  };
  let i = 0;
  let j = 0;
  while (i < a.length && j < b.length) {
    if (a[i] === b[j]) {
      push('equal', a[i]!);
      i += 1;
      j += 1;
    } else if (lcs[(i + 1) * width + j]! >= lcs[i * width + j + 1]!) {
      push('delete', a[i]!);
      i += 1;
    } else {
      push('insert', b[j]!);
      j += 1;
    }
  }
  while (i < a.length) {
    push('delete', a[i]!);
    i += 1;
  }
  while (j < b.length) {
    push('insert', b[j]!);
    j += 1;
  }
  return out;
}

/** Did anything at all move? Cheap enough to call before rendering a diff. */
export function hasTextChange(before: string, after: string): boolean {
  return before !== after;
}
